import type { TFunction } from "i18next"
import type { Palette } from "@mui/material"

import type { BarPlotView } from "types/dashboard.types"
import type { MuiBarConfig } from "types/muiBar.types"

interface muiBarConfigParams {
  view: BarPlotView
  palette: Palette
  t: TFunction
}

export default function muiBarConfig({
  view,
  palette,
  t,
}: muiBarConfigParams): MuiBarConfig {
  const viewProperties = view.properties
  const dataset = viewProperties.demoData ?? []
  const seriesConfigs = viewProperties.series ?? []
  const fallbackColors = viewProperties.colors

  const axisColor = palette.neutral[600]

  const series = seriesConfigs.map((s, index) => ({
    type: "bar" as const,
    id: s.dataKey,
    dataKey: s.dataKey,
    label: t(`plot_titles.${s.labelKey}`, { defaultValue: s.labelKey }),
    color: s.color ?? fallbackColors[index % fallbackColors.length],
    valueFormatter: (value: number | null) =>
      value !== null
        ? `${value.toFixed(viewProperties.decimalPlaces ?? 1)}${viewProperties.suffix ?? "%"}`
        : null,
  }))

  const xAxis = [
    {
      scaleType: "band" as const,
      dataKey: "label",
      tickLabelStyle: {
        fill: axisColor,
        fontWeight: 500,
        fontSize: 10,
      },
    },
  ]

  const yAxis = [
    {
      valueFormatter: (value: number) =>
        `${value.toFixed(0)}${viewProperties.suffix ?? "%"}`,
      tickLabelStyle: {
        fill: axisColor,
        fontWeight: 500,
        fontSize: 10,
      },
    },
  ]

  const grid = viewProperties.grid
  const gridStyle = viewProperties.gridStyle ?? {
    stroke: palette.neutral[900],
    opacity: 0.05,
    strokeDasharray: "3, 3",
  }
  const margin = viewProperties.margin
  const yReferenceLines = viewProperties.yReferenceLines
  const legend = viewProperties.legend

  return {
    series,
    dataset,
    xAxis,
    yAxis,
    margin,
    grid,
    gridStyle,
    yReferenceLines,
    legend,
    axisColor,
  }
}
